import { createContext, useContext } from 'react'

import type {
  DashboardContextBuilder,
  DashboardFocusChip,
} from 'features/dashboards-v2-1/types/dashboard-page-context'

export interface DashboardPageRegistrationType {
  /** Called on mount with a stable builder the widget runs at send time. */
  registerDashboardContextBuilder: (builder: DashboardContextBuilder) => void
  /**
   * Called on unmount with the SAME builder. Compare-and-clear: it clears only
   * if that builder is still the registered one, so an overlapping unmount of
   * one page can't wipe the builder the next page just registered.
   */
  unregisterDashboardContextBuilder: (builder: DashboardContextBuilder) => void
  /** Publishes what the user is looking at, for the composer chip. */
  publishDashboardFocus: (chip: DashboardFocusChip | null) => void
  /** Compare-and-clear, like `unregisterDashboardContextBuilder`. */
  clearDashboardFocus: (chip: DashboardFocusChip | null) => void
}

/**
 * Where a 2.1 dashboard registers how to describe itself to Ask Yogi.
 *
 * A separate, never-changing context for the same reason as
 * `ChartPageRegistrationContext`: the page must not re-render whenever the
 * wide Ask Yogi value re-memoises. The builder is a ref the widget runs when
 * a message is sent, so it is never stale and never costs a render. The focus
 * chip is published as state, read only through `useDashboardFocus`.
 */
export const DashboardPageRegistrationContext =
  createContext<DashboardPageRegistrationType | null>(null)

/** Null outside the provider (the public share page), never throws. */
export const useDashboardPageRegistration =
  (): DashboardPageRegistrationType | null =>
    useContext(DashboardPageRegistrationContext)
